import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

type UseCarouselDotButtonsResult = {
    selectedIndex: number;
    scrollSnaps: number[];
    onDotButtonClick: (index: number) => void;
};

/**
 * React hook for dot-based pagination controls in Embla Carousel.
 *
 * Connects dot buttons to an Embla instance, tracks the selected slide and available scroll snaps,
 * and provides a handler to scroll to a specific slide on dot button click.
 *
 * @param {EmblaCarouselType} emblaApi - Optional Embla Carousel API instance.
 * @returns {{
 *   selectedIndex: number,
 *   scrollSnaps: number[],
 *   onDotButtonClick: (index: number) => void
 * }} Selected slide index, scroll snap indices, and dot click handler.
 */
export function useCarouselDotButtons(emblaApi?: EmblaCarouselType): UseCarouselDotButtonsResult {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const handleDotButtonClick = useCallback(
        (index: number) => {
            emblaApi?.scrollTo(index);
        },
        [emblaApi]
    );

    const updateScrollSnaps = useCallback((embla: EmblaCarouselType) => {
        setScrollSnaps(embla.scrollSnapList());
    }, []);

    const updateSelectedIndex = useCallback((embla: EmblaCarouselType) => {
        setSelectedIndex(embla.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) {
            return undefined;
        }

        // Set initial values
        updateScrollSnaps(emblaApi);
        updateSelectedIndex(emblaApi);

        // Event listeners
        const handleReInit = () => {
            updateScrollSnaps(emblaApi);
            updateSelectedIndex(emblaApi);
        };
        const handleSelect = () => {
            updateSelectedIndex(emblaApi);
        };

        emblaApi.on('reInit', handleReInit);
        emblaApi.on('select', handleSelect);

        return () => {
            emblaApi?.off('reInit', handleReInit);
            emblaApi?.off('select', handleSelect);
        };
    }, [emblaApi, updateScrollSnaps, updateSelectedIndex]);

    return {
        selectedIndex,
        scrollSnaps,
        onDotButtonClick: handleDotButtonClick,
    };
}
